import {
  GetHealthcareByIdItem,
  GetHealthcareByIdProductItem,
  GetHealthcareByLevel2Item,
  GetHealthcareByLevel4Item,
  GetServicesCarouselDataModel,
} from '@models/clinics';
import {
  MEDICAL_SERVICE_GROUP_ALIASES,
  MEDICAL_SERVICE_GROUPS,
} from '@constant/medical-services';
import {
  MedicalServiceGroup,
  MedicalServiceLevel2,
  ServicesCarouselModel,
} from '@models/medical-services';

export const mapClinicCarouselToMedicalCarousel = (
  carousel: GetServicesCarouselDataModel,
): ServicesCarouselModel => ({
  ...carousel,
  subTitleId: carousel.subTitleId ? Number(carousel.subTitleId) : null,
  link: carousel.link ?? null,
  details: carousel.details.map((detail) => ({
    ...detail,
    servicesCarouselId: detail.servicesCarouselId,
  })),
});

export const mapClinicCarouselsToMedicalCarousels = (
  carousels: GetServicesCarouselDataModel[] = [],
): ServicesCarouselModel[] => carousels.map(mapClinicCarouselToMedicalCarousel);

export const getHealthcareGroupItem = (
  queryResult?: GetHealthcareByIdItem[],
): GetHealthcareByIdItem | undefined => queryResult?.[0];

const normalizeHealthcareName = (value?: string | null) =>
  (value ?? '')
    .replace(/\u200c/g, '')
    .replace(/[،,]/g, ' ')
    .replace(/\s+/g, '')
    .trim()
    .toLowerCase();

const healthcareGroupSlugByName = Object.entries(MEDICAL_SERVICE_GROUPS).reduce<
  Record<string, string>
>((groups, [slug, group]) => {
  [group.title, ...(MEDICAL_SERVICE_GROUP_ALIASES[slug] ?? [])].forEach(
    (alias) => {
      groups[normalizeHealthcareName(alias)] = slug;
    },
  );

  return groups;
}, {});

const getAliasesForSlug = (slug: string) => {
  const group = MEDICAL_SERVICE_GROUPS[slug];
  return [
    ...(group ? [group.title] : []),
    ...(MEDICAL_SERVICE_GROUP_ALIASES[slug] ?? []),
  ];
};

export const matchesHealthcareGroupSlug = (
  name: string,
  slugOrTitle: string,
) => {
  const normalizedName = normalizeHealthcareName(name);
  const slug =
    MEDICAL_SERVICE_GROUPS[slugOrTitle]?.slug ??
    healthcareGroupSlugByName[normalizeHealthcareName(slugOrTitle)];

  if (!slug) {
    return normalizedName === normalizeHealthcareName(slugOrTitle);
  }

  return healthcareGroupSlugByName[normalizedName] === slug;
};

type HealthcareNamedItem = {
  id: number;
  name: string;
  products?: GetHealthcareByIdProductItem[];
  children?: GetHealthcareByIdItem[];
};

export const findHealthcareGroupByRecId = <T extends HealthcareNamedItem>(
  recId: string,
  items: T[] = [],
): T | undefined => {
  if (!recId) {
    return undefined;
  }

  return items.find((item) => String(item.id) === recId);
};

export const findHealthcareGroupBySlug = <T extends HealthcareNamedItem>(
  slugOrId: string,
  items: T[] = [],
): T | undefined => {
  if (/^\d+$/.test(slugOrId)) {
    const byId = items.find((item) => String(item.id) === slugOrId);
    if (byId) {
      return byId;
    }
  }

  return items.find((item) => matchesHealthcareGroupSlug(item.name, slugOrId));
};

type HealthcareProductItem = {
  id: number;
  name: string;
  code: string;
  categoryImage?: string | null;
  avatarImage?: string | null;
  url?: string | null;
  isActive?: boolean;
};

export const mapHealthcareProductsToLevel2 = (
  products: HealthcareProductItem[] = [],
): MedicalServiceLevel2[] =>
  products
    .filter((item) => item.isActive !== false)
    .map((item) => ({
      id: item.id,
      title: item.name,
      slug: String(item.id),
      code: item.code,
      imageUrl: item.avatarImage || item.url || item.categoryImage || null,
    }));

export const filterHealthcareChildrenByProduct = (
  children: MedicalServiceLevel2[] = [],
  product?: MedicalServiceLevel2,
): MedicalServiceLevel2[] => {
  if (!product) {
    return [];
  }

  if (product.id !== undefined) {
    const childrenByProductId = children.filter(
      (child) => child.productId === product.id,
    );

    if (childrenByProductId.length) {
      return childrenByProductId;
    }
  }

  if (!product.code) {
    return [];
  }

  const codePrefix = `${product.code}_`;

  return children.filter((child) => child.code?.startsWith(codePrefix));
};

export const resolveProductSlugForServiceName = (
  serviceName: string,
  products: MedicalServiceLevel2[] = [],
  children: MedicalServiceLevel2[] = [],
): string | null => {
  if (!serviceName) {
    return null;
  }

  const normalized = normalizeHealthcareName(serviceName);

  const findParentByChild = (child?: MedicalServiceLevel2) => {
    if (!child) {
      return null;
    }

    if (child.productId !== undefined) {
      const parentByProductId = products.find(
        (product) => product.id === child.productId,
      );

      if (parentByProductId) {
        return parentByProductId.slug;
      }
    }

    if (!child.code) {
      return null;
    }

    return (
      products.find((product) => child.code!.startsWith(`${product.code}_`))
        ?.slug ?? null
    );
  };

  const exactChild = children.find(
    (child) => normalizeHealthcareName(child.title) === normalized,
  );
  const parentFromExactChild = findParentByChild(exactChild);
  if (parentFromExactChild) {
    return parentFromExactChild;
  }

  const exactProduct = products.find(
    (product) => normalizeHealthcareName(product.title) === normalized,
  );
  if (exactProduct) {
    return exactProduct.slug;
  }

  const partialChild = children.find((child) => {
    const childName = normalizeHealthcareName(child.title);

    return childName.includes(normalized) || normalized.includes(childName);
  });
  const parentFromPartialChild = findParentByChild(partialChild);
  if (parentFromPartialChild) {
    return parentFromPartialChild;
  }

  if (
    normalized.includes('بوتاکس') ||
    normalized.includes('ژل') ||
    normalized.includes('تزریق')
  ) {
    return (
      products.find((product) =>
        normalizeHealthcareName(product.title).includes('تزریق'),
      )?.slug ?? null
    );
  }

  if (normalized.includes('لیزر')) {
    return (
      products.find((product) =>
        normalizeHealthcareName(product.title).includes('لیزر'),
      )?.slug ?? null
    );
  }

  if (normalized.includes('کاشت') || normalized.includes('مو')) {
    return (
      products.find((product) =>
        normalizeHealthcareName(product.title).includes('مووابرو'),
      )?.slug ?? null
    );
  }

  return null;
};

export const mapHealthcareLevel2ToGroups = (
  items: GetHealthcareByLevel2Item[] = [],
): MedicalServiceGroup[] =>
  items
    .filter((item) => item.isActive)
    .map((item) => {
      const matchedGroup =
        MEDICAL_SERVICE_GROUPS[
          healthcareGroupSlugByName[normalizeHealthcareName(item.name)]
        ];

      return {
        title: item.name,
        slug: matchedGroup?.slug ?? String(item.id),
        id: String(item.id),
        imageUrl: item.imageUrlLevel2 || item.categoryImage || null,
      };
    });

export const resolveHealthcareGroupIdBySlug = (
  items: GetHealthcareByLevel2Item[] = [],
  groupSlug: string,
): string | null =>
  mapHealthcareLevel2ToGroups(items).find((group) => group.slug === groupSlug)
    ?.id ?? null;

export const mapHealthcareItemsToLevel2 = (
  items: Array<
    | GetHealthcareByIdItem
    | GetHealthcareByLevel4Item
    | GetHealthcareByLevel2Item
  > = [],
): MedicalServiceLevel2[] =>
  items.map((item) => {
    const {
      id,
      name,
      code,
      description,
      categoryImage,
      imageUrlLevel2,
      imageUrlLevel4,
      productId,
      doctorName,
      price,
      discountPrice,
      discountPercent,
      rate,
      isInstalments,
      instalmentCount,
      url,
      deposit,
      profileType,
      profileId,
      profileOwnerId,
      serviceId,
    } = item as GetHealthcareByIdItem;

    return {
      id,
      title: name,
      slug: String(id),
      code,
      productId,
      imageUrl: imageUrlLevel2 || categoryImage || null,
      imageUrlLevel4: imageUrlLevel4 || null,
      description,
      doctorName,
      price,
      discountPrice,
      discountPercent,
      deposit,
      rate,
      isInstalments,
      instalmentCount,
      url: url ?? null,
      profileType,
      profileId,
      profileOwnerId,
      serviceId,
    };
  });

export const getGroupLevel2Items = (
  group?: GetHealthcareByIdItem | HealthcareNamedItem,
  level4Items: GetHealthcareByLevel4Item[] = [],
  level2Items: GetHealthcareByLevel2Item[] = [],
): MedicalServiceLevel2[] => {
  if (group?.products?.length) {
    return mapHealthcareProductsToLevel2(group.products);
  }

  if (group?.children?.length) {
    return mapHealthcareItemsToLevel2(group.children);
  }

  if (group?.id) {
    const filteredLevel2 = level2Items.filter(
      (item) => item.parentId === group.id,
    );

    if (filteredLevel2.length) {
      return mapHealthcareItemsToLevel2(filteredLevel2);
    }

    return mapHealthcareItemsToLevel2(
      level4Items.filter((item) => item.parentId === group.id),
    );
  }

  return [];
};
